import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { api, formatApiError, fmtDate, money } from "@/lib/api";
import { CLAIM_LABEL, claimTypeLabel, coverageLabel } from "@/lib/constants";

const ClaimEdit = ({ c, onDone }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(c.status);
  const [note, setNote] = useState("");
  const [payout, setPayout] = useState(c.payout ?? "");
  const [adjuster, setAdjuster] = useState(c.adjuster || "");
  const [internal, setInternal] = useState(c.internal_notes || "");
  const save = async () => {
    try {
      await api.patch(`/admin/claims/${c.id}`, { status, note, payout: payout === "" ? null : Number(payout), adjuster, internal_notes: internal });
      toast.success("Claim updated");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`admin-claim-edit-${c.id}`}>Review</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="admin-claim-dialog">
        <DialogHeader><DialogTitle>{c.claim_number}</DialogTitle></DialogHeader>
        <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-sm">
          <p><b>{c.insured_name}</b> · {c.user_email}</p>
          <p>{coverageLabel(c.coverage_type)} · Policy {c.policy_number}</p>
          <p>{claimTypeLabel(c.coverage_type, c.incident_type)} · {fmtDate(c.incident_date)} · {c.location}</p>
          <p className="whitespace-pre-wrap">{c.description}</p>
          {c.police_report && <p>Report #: {c.police_report}</p>}
          {c.other_party && <p>Other party: {c.other_party}</p>}
          <p>Injuries: {c.injuries ? "Yes" : "No"}</p>
        </div>
        <div><p className="mb-2 text-sm font-semibold">Claim photos & reports</p><DocumentsPanel policyId={c.policy_id} claimId={c.id} canUpload={false} compact /></div>
        <div className="grid gap-3">
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-claim-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CLAIM_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Assigned adjuster</Label><Input value={adjuster} onChange={(e) => setAdjuster(e.target.value)} placeholder="Adjuster name" data-testid="admin-claim-adjuster-input" /></div>
          <div className="space-y-1.5"><Label>Internal adjuster notes (not shown to customer)</Label><Textarea rows={3} value={internal} onChange={(e) => setInternal(e.target.value)} data-testid="admin-claim-internal-input" /></div>
          <div className="space-y-1.5"><Label>Payout amount (optional)</Label><Input type="number" min="0" value={payout} onChange={(e) => setPayout(e.target.value)} data-testid="admin-claim-payout-input" /></div>
          <div className="space-y-1.5"><Label>Note to customer</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-claim-note-input" /></div>
          <Button onClick={save} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="admin-claim-save-btn">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AdminClaims = ({ search }) => {
  const [claims, setClaims] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/claims").then((r) => setClaims(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const s = search.toLowerCase();
  const rows = claims.filter((c) => !s || [c.claim_number, c.insured_name, c.user_email, c.policy_number].some((v) => v.toLowerCase().includes(s)));
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="admin-claims-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Filed", "Claim #", "Insured", "Policy", "What happened", "Incident", "Payout", "Adjuster", "Status", ""].map((h) => <th key={h} className="whitespace-nowrap p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-100" data-testid="admin-claim-row">
              <td className="whitespace-nowrap p-3">{fmtDate(c.created_at)}</td>
              <td className="whitespace-nowrap p-3 font-mono">{c.claim_number}</td>
              <td className="whitespace-nowrap p-3"><p className="font-semibold">{c.insured_name}</p><p className="text-xs text-slate-500">{c.user_email}</p></td>
              <td className="whitespace-nowrap p-3 font-mono">{c.policy_number}</td>
              <td className="whitespace-nowrap p-3">{claimTypeLabel(c.coverage_type, c.incident_type)}{c.injuries && <span className="ml-1 text-xs text-red-600">· injuries</span>}</td>
              <td className="whitespace-nowrap p-3">{fmtDate(c.incident_date)}</td>
              <td className="whitespace-nowrap p-3 font-mono">{c.payout != null ? money(c.payout) : "—"}</td>
              <td className="whitespace-nowrap p-3" data-testid={`admin-claim-adjuster-${c.id}`}>{c.adjuster || <span className="text-amber-700">Unassigned</span>}</td>
              <td className="whitespace-nowrap p-3"><StatusPill status={c.status} label={CLAIM_LABEL[c.status]} /></td>
              <td className="whitespace-nowrap p-3"><ClaimEdit c={c} onDone={load} /></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={10} className="p-6 text-center text-slate-500" data-testid="admin-claims-empty">No claims yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
